// LandingPage.js - Main landing page for Travel+ CRM
import React, { useState } from 'react';

const LandingPage = () => {
  const [activeTab, setActiveTab] = useState('login');
  const [selectedAgency, setSelectedAgency] = useState('');
  const [showAgencyDropdown, setShowAgencyDropdown] = useState(false);
  const [showJoinDropdown, setShowJoinDropdown] = useState(false);

  const agencies = [
    { code: 'MST', name: 'MainStreet Travel Agency', color: 'blue' },
    { code: 'YETI', name: 'Yeti Travel Co', color: 'green' },
    { code: 'BWK', name: 'Boardwalk Travel', color: 'purple' }
  ];

  const handleAgencyLogin = () => {
    if (selectedAgency === 'independent') {
      window.location.href = '/';
    } else if (selectedAgency) {
      window.location.href = `/${selectedAgency.toLowerCase()}/login`;
    }
  };

  const handleJoinAgency = (agencyCode) => {
    if (agencyCode === 'independent') {
      window.location.href = '/register/independent';
    } else if (agencyCode === 'agency') {
      window.location.href = '/join-as-agency';
    } else {
      window.location.href = `/${agencyCode.toLowerCase()}/register`;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-center">
            {/* Logo */}
            <div className="flex items-center">
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg p-2 mr-3">
                <span className="text-white font-bold text-2xl">T+</span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900">Travel+ CRM</h1>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-8">
          <h2 className="text-4xl font-extrabold text-gray-900 mb-4">
            Welcome to Travel+ CRM
          </h2>
          <p className="text-xl text-gray-600">
            The comprehensive travel agency management system for modern travel professionals
          </p>
        </div>

        {/* Login/Join Card */}
        <div className="bg-white rounded-lg shadow-xl overflow-hidden">
          {/* Tabs */}
          <div className="flex border-b">
            <button
              onClick={() => setActiveTab('login')}
              className={`flex-1 py-4 px-6 text-center font-medium transition-colors ${
                activeTab === 'login'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
              }`}
            >
              Agent Login
            </button>
            <button
              onClick={() => setActiveTab('join')}
              className={`flex-1 py-4 px-6 text-center font-medium transition-colors ${
                activeTab === 'join'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
              }`}
            >
              Join Now
            </button>
          </div>

          {/* Tab Content */}
          <div className="p-8">
            {activeTab === 'login' ? (
              <div className="space-y-6">
                <h3 className="text-2xl font-semibold text-center text-gray-900">
                  Select Your Agency or Login as Independent Agent
                </h3>
                
                {/* Agency Dropdown */}
                <div className="max-w-md mx-auto">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Choose your agency:
                  </label>
                  <select
                    value={selectedAgency}
                    onChange={(e) => setSelectedAgency(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">-- Select Agency --</option>
                    <option value="independent">Independent Agent</option>
                    <optgroup label="Partner Agencies">
                      {agencies.map((agency) => (
                        <option key={agency.code} value={agency.code}>
                          {agency.name}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                </div>

                {/* Login Button */}
                <div className="max-w-md mx-auto">
                  <button
                    onClick={handleAgencyLogin}
                    disabled={!selectedAgency}
                    className="w-full py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Continue to Login
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <h3 className="text-2xl font-semibold text-center text-gray-900">
                  Join Travel+ CRM
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto">
                  {/* Join Under Agency */}
                  <div className="bg-gray-50 rounded-lg p-6 text-center">
                    <div className="mb-4">
                      <div className="mx-auto h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                        <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                      </div>
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-2">Join an Agency</h4>
                    <p className="text-sm text-gray-600 mb-4">Apply to join one of our partner agencies</p>
                    <div className="relative">
                      <button
                        onClick={() => setShowJoinDropdown(!showJoinDropdown)}
                        className="w-full py-2 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                      >
                        Select Agency ▼
                      </button>
                      {showJoinDropdown && (
                        <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md border border-gray-200">
                          {agencies.map((agency) => (
                            <button
                              key={agency.code}
                              onClick={() => {
                                handleJoinAgency(agency.code);
                                setShowJoinDropdown(false);
                              }}
                              className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              {agency.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Join as Independent */}
                  <div className="bg-gray-50 rounded-lg p-6 text-center">
                    <div className="mb-4">
                      <div className="mx-auto h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
                        <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-2">Independent Agent</h4>
                    <p className="text-sm text-gray-600 mb-4">Start your own travel business with our tools</p>
                    <button
                      onClick={() => handleJoinAgency('independent')}
                      className="w-full py-2 px-4 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700"
                    >
                      Join as Independent
                    </button>
                  </div>

                  {/* Join as Agency */}
                  <div className="bg-gray-50 rounded-lg p-6 text-center">
                    <div className="mb-4">
                      <div className="mx-auto h-12 w-12 bg-purple-100 rounded-full flex items-center justify-center">
                        <svg className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                      </div>
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-2">Join as Agency</h4>
                    <p className="text-sm text-gray-600 mb-4">Manage your agents with Travel+ CRM</p>
                    <button
                      onClick={() => handleJoinAgency('agency')}
                      className="w-full py-2 px-4 border border-transparent rounded-md text-sm font-medium text-white bg-purple-600 hover:bg-purple-700"
                    >
                      Learn More
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Features Section */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg p-6 text-center shadow">
            <div className="text-blue-600 mb-4">
              <svg className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
              </svg>
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Quote Management</h3>
            <p className="text-sm text-gray-600">Create and manage travel quotes with ease</p>
          </div>

          <div className="bg-white rounded-lg p-6 text-center shadow">
            <div className="text-green-600 mb-4">
              <svg className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Client Management</h3>
            <p className="text-sm text-gray-600">Keep track of all your clients in one place</p>
          </div>

          <div className="bg-white rounded-lg p-6 text-center shadow">
            <div className="text-purple-600 mb-4">
              <svg className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Commission Tracking</h3>
            <p className="text-sm text-gray-600">Monitor and manage your commissions</p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="mt-auto bg-white border-t">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-2">
            <div className="text-sm text-gray-500">
              <a href="/admin" className="hover:text-gray-700 underline mr-4">
                Super Admin Login
              </a>
              <span className="text-gray-400">|</span>
              <a href="/mst/admin" className="hover:text-gray-700 underline ml-4 mr-2">
                Agency Admin Login
              </a>
            </div>
            <p className="text-xs text-gray-400">
              © 2024 Travel+ CRM. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
